import AntEllipsisOutlined from "@ant-design/icons/EllipsisOutlined";
import {
  KICKED_GROUP_STATUSES,
  NOT_IN_GROUP_STATUSES,
  USER_MEMBERSHIP_UNAUTHORIZED_STATUS,
} from "common/constants";
import { getDayjsLifetimeDate } from "@shared/utils/date";
import moment from "moment";
import { FixMeLater } from "types";
import { useEffect, useState } from "react";
import { openMessage } from "utils/misc";
import { getTelegramUserLink } from "features/admin/group/utils";
import styled, { useTheme } from "styled-components";
import RowActionsTrigger from "features/admin/NewLook/RowActionsTrigger";
import { ItemType } from "antd/es/menu/interface";
import Dropdown from "common/Dropdown/Dropdown";
import { sharedLib } from "@shared/services/shared.service";
import { Dayjs } from "dayjs";
import { LanguageNameSpaces, SubGroupUserMembership } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface ActionsDropdownProps {
  membership: SubGroupUserMembership;
  subGroupID: number;
  updateExpiration: (membership: SubGroupUserMembership, date: Dayjs) => void;
  confirmWithCallback: (confirmStr: string, onOkCallback: () => void) => void;
  setTableIsLoading: (isTableLoading: boolean) => void;
}

const ActionsDropdown = ({
  membership,
  subGroupID,
  updateExpiration,
  confirmWithCallback,
  setTableIsLoading,
}: ActionsDropdownProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const { isNewLook } = useTheme();
  const [authorizeUser, authorizeUserStatus] =
    sharedLib.sharedApi.admin.useAuthorizeUserMutation();
  const [kickUser, kickUserStatus] =
    sharedLib.sharedApi.admin.useKickUserMutation();
  const setLifetime = (membership: SubGroupUserMembership) => {
    updateExpiration(membership, getDayjsLifetimeDate());
  };
  const telegramUserLink = getTelegramUserLink(membership.telegram_user);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    setTableIsLoading(
      authorizeUserStatus.isLoading || kickUserStatus.isLoading
    );
  }, [authorizeUserStatus, kickUserStatus]);

  const handleAuthorizeUser = () => {
    authorizeUser({
      sub_group_id: subGroupID,
      membership_id: membership.id,
    })
      .unwrap()
      .then(() => openMessage(t("users.actions.authorizeSuccess"), "success"))
      .catch((error) => {
        openMessage(
          error?.data?.message || t("users.actions.authorizeError"),
          "error",
          8
        );
      });
  };

  const handleKickUser: FixMeLater = () => {
    kickUser({
      sub_group_id: subGroupID,
      membership_id: membership.id,
    })
      .unwrap()
      .then(() => openMessage(t("users.actions.kickSuccess"), "success"))
      .catch((error) => {
        openMessage(
          error?.data?.message || t("users.actions.kickError"),
          "error",
          8
        );
      });
  };

  const handleAuthorizeWithConfirm = async () => {
    const parsedExpiration = moment.utc(membership.expiration_datetime);
    let dateIsBeforeNow = moment.utc().isAfter(parsedExpiration);
    if (dateIsBeforeNow) {
      confirmWithCallback(
        t("users.confirm.confirm_authorize_date_less_than_now"),
        handleAuthorizeUser
      );
    } else {
      handleAuthorizeUser();
    }
  };

  const closeDropdown = () => setDropdownOpen(false);

  const menuItems: ItemType[] = [
    {
      key: "lifetime",
      label: t("users.actions.lifetime"),
      onClick: () => {
        closeDropdown();
        setLifetime(membership);
      },
    },
    {
      key: "chat",
      label: (
        <a href={telegramUserLink} target="_blank">
          {t("users.actions.chat")}
        </a>
      ),
      onClick: closeDropdown,
      disabled: !telegramUserLink,
    },
  ];

  if (KICKED_GROUP_STATUSES.indexOf(membership.status) === -1)
    menuItems.push({
      key: "kick",
      label:
        NOT_IN_GROUP_STATUSES.indexOf(membership.status) > -1
          ? t("users.actions.ban")
          : t("users.actions.kick"),
      danger: true,
      onClick: () => {
        closeDropdown();
        handleKickUser();
      },
    });
  if (membership.status === USER_MEMBERSHIP_UNAUTHORIZED_STATUS)
    menuItems.push({
      key: "authorize",
      label: t("users.actions.authorize"),
      onClick: () => {
        closeDropdown();
        handleAuthorizeWithConfirm();
      },
      // New look: colour is for meaning; only the destructive item is red.
      className: isNewLook ? undefined : "success",
    });
  if (KICKED_GROUP_STATUSES.indexOf(membership.status) > -1)
    menuItems.push({
      key: "unban",
      label: t("users.actions.unban"),
      onClick: () => {
        closeDropdown();
        handleAuthorizeWithConfirm();
      },
      // New look: colour is for meaning; only the destructive item is red.
      className: isNewLook ? undefined : "success",
    });

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown
        title="Actions"
        menu={{ items: menuItems }}
        trigger={["click"]}
        onOpenChange={setDropdownOpen}
        open={dropdownOpen}
      >
        {isNewLook ? <RowActionsTrigger /> : <EllipsisOutlined />}
      </Dropdown>
    </div>
  );
};

export default ActionsDropdown;

const EllipsisOutlined = styled(AntEllipsisOutlined)`
  cursor: pointer;
  font-size: 30px;
  transform: rotate(90deg);
`;
